/**
 * Emissions Activities Page
 *
 * List and manage emission activity records.
 * Based on CarbonIQ Scope pages design pattern.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Plus, CheckSquare, X, Download, Trash2, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { FiscalYearSelector, getFiscalYearPeriod } from '../components/FiscalYearSelector';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent, CardTitle, CardHeader } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { downloadBlob } from '@utils/downloadBlob';
import { useAppSelector } from '../../../reducers';
import { getInitiativeTypeTree } from '../../../selectors/initiativeTree';
import { IncludeChildrenToggle } from '../components/IncludeChildrenToggle';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl } from '@routes/util';
import { DataQualityCard } from '../components/DataQualityCard';
import { CategoryBreakdownPanel } from '../components/CategoryBreakdownPanel';
import { ActivityFilters, ActivityFilterValues } from '../components/ActivityFilters';
import { ActivityTable } from '../components/ActivityTable';
import { EmptyStateCard } from '../components/EmptyStateCard';
import { CalculationDetailsDrawer } from '../components/CalculationDetailsDrawer';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import {
  useGetActivitiesQuery,
  useDeleteActivityMutation,
  useTriggerCalculationMutation,
  useExportActivitiesMutation,
  useBulkDeleteActivitiesMutation,
} from '../api/emissions';
import { EmissionScope, type ActivityRecord } from '../types/emissions';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

type ConfirmAction = 'delete' | 'recalculate' | 'bulk-delete';

interface ConfirmModalState {
  isOpen: boolean;
  action: ConfirmAction | null;
  activityId: string | null;
  activityInitiativeId: string | null;
}

const DEFAULT_FILTERS: ActivityFilterValues = {
  search: '',
  scope: '',
  activityType: '',
  status: '',
  assetId: '',
  periodStart: '',
  periodEnd: '',
  bulkImportIds: '',
};

const buildDefaultFilters = (fiscalYear: number): ActivityFilterValues => {
  const period = getFiscalYearPeriod(fiscalYear);
  return { ...DEFAULT_FILTERS, periodStart: period.start, periodEnd: period.end };
};

const DEFAULT_CONFIRM_STATE: ConfirmModalState = {
  isOpen: false,
  action: null,
  activityId: null,
  activityInitiativeId: null,
};

const PAGE_SIZE = 25;

export const EmissionsActivitiesPage = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const history = useHistory();
  const { search } = useLocation();
  const { hasChildren, selectedFiscalYear, setSelectedFiscalYear, availableYears, includeChildren, setIncludeChildren } = useEmissionsFilters();
  const initiativeTypeTree = useAppSelector(getInitiativeTypeTree);

  const initiativeLabelMap = useMemo(
    () => new Map(initiativeTypeTree.map((i) => [i._id, i.name])),
    [initiativeTypeTree]
  );

  const getReportingLevelLabel = useCallback(
    (activityInitiativeId: string) =>
      activityInitiativeId === initiativeId
        ? <Badge variant='default'>Current Level</Badge>
        : <Badge variant='light'>{initiativeLabelMap.get(activityInitiativeId) ?? activityInitiativeId}</Badge>,
    [initiativeId, initiativeLabelMap]
  );

  const getInitialFilters = (): ActivityFilterValues => {
    const params = new URLSearchParams(search);
    const defaults = buildDefaultFilters(selectedFiscalYear);
    const scope = params.get('scope');
    const status = params.get('status');
    const periodStart = params.get('periodStart');
    const periodEnd = params.get('periodEnd');
    const bulkImportIds = params.get('bulkImportIds');
    return {
      ...defaults,
      ...(scope && { scope }),
      ...(status && { status }),
      ...(periodStart && { periodStart }),
      ...(periodEnd && { periodEnd }),
      ...(bulkImportIds && { bulkImportIds }),
    };
  };

  const getInitialSortField = () => new URLSearchParams(search).get('sort') ?? 'periodStart';
  const getInitialSortDir = (): 'asc' | 'desc' =>
    (new URLSearchParams(search).get('sortDir') as 'asc' | 'desc') ?? 'desc';

  // Seed shared filter context from deep-link URL params once on mount. After this the context
  // is authoritative; URL params are no longer consulted for these values.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const periodStartParam = params.get('periodStart');
    const includeChildrenParam = params.get('includeChildren');
    if (periodStartParam) {
      const year = new Date(periodStartParam).getFullYear();
      if (!Number.isNaN(year) && year !== selectedFiscalYear) setSelectedFiscalYear(year);
    }
    if (includeChildrenParam !== null) {
      const value = includeChildrenParam === 'true';
      if (value !== includeChildren) setIncludeChildren(value);
    }
  }, []);

  const [filters, setFilters] = useState<ActivityFilterValues>(getInitialFilters);
  const [selectedActivity, setSelectedActivity] = useState<ActivityRecord | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(DEFAULT_CONFIRM_STATE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [recalculatingActivityId, setRecalculatingActivityId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(getInitialSortField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(getInitialSortDir);

  // Debounce search input to avoid firing API calls on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset to first page when server-side filters or sort change
  useEffect(() => {
    setPage(0);
  }, [filters.scope, filters.activityType, filters.status, filters.assetId, filters.bulkImportIds, filters.periodStart, filters.periodEnd, debouncedSearch, sortField, sortDir]);
  const [exportActivities, { isLoading: isExporting }] = useExportActivitiesMutation();
  const [bulkDeleteActivities, { isLoading: isBulkDeleting }] = useBulkDeleteActivitiesMutation();

  const {
    data: activitiesData,
    isLoading,
    isFetching,
  } = useGetActivitiesQuery({
    initiativeId,
    scope: filters.scope ? (filters.scope as EmissionScope) : undefined,
    activityType: filters.activityType || undefined,
    assetId: filters.assetId || undefined,
    bulkImportIds: filters.bulkImportIds || undefined,
    calculationStatus: filters.status || undefined,
    search: debouncedSearch || undefined,
    periodStart: filters.periodStart || undefined,
    periodEnd: filters.periodEnd || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    includeChildren,
    sort: sortField,
    sortDir,
  });

  const [deleteActivity, { isLoading: isDeleting }] = useDeleteActivityMutation();
  const [triggerCalculation, { isLoading: isRecalculating }] = useTriggerCalculationMutation();

  const activities = useMemo(() => activitiesData?.data ?? [], [activitiesData?.data]);

  // A "non-period" filter is anything the user set beyond the fiscal year window.
  // Period dates are auto-driven by the fiscal year selector and don't count.
  const hasNonPeriodFilters =
    Boolean(filters.search) ||
    Boolean(filters.scope) ||
    Boolean(filters.activityType) ||
    Boolean(filters.status) ||
    Boolean(filters.assetId) ||
    Boolean(filters.bulkImportIds);

  const resetNonPeriodFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: '',
      scope: '',
      activityType: '',
      status: '',
      assetId: '',
      bulkImportIds: '',
    }));
  };

  const handleFiscalYearChange = (year: number) => {
    setSelectedFiscalYear(year);
    const period = getFiscalYearPeriod(year);
    setFilters((prev) => ({ ...prev, periodStart: period.start, periodEnd: period.end }));
  };

  const newActivityUrl = generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, {
    rootAppPath,
    initiativeId,
  });

  const handleEdit = (activityId: string, activityInitiativeId: string) => {
    const editUrl = generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_EDIT, {
      rootAppPath,
      initiativeId: activityInitiativeId,
      activityId,
    });
    history.push(editUrl);
  };

  const handleDelete = (activityId: string) => {
    setConfirmModal({ isOpen: true, action: 'delete', activityId, activityInitiativeId: null });
  };

  const handleRecalculate = async (activityId: string, activityInitiativeId: string) => {
    const activity = activities.find((a) => a._id === activityId);
    if (activity?.calculatedCO2e !== undefined && activity.calculatedCO2e !== null) {
      setConfirmModal({ isOpen: true, action: 'recalculate', activityId, activityInitiativeId });
      return;
    }
    setRecalculatingActivityId(activityId);
    try {
      await triggerCalculation({ initiativeId: activityInitiativeId, activityId, options: { recalculate: true } }).unwrap();
      generateToast({ color: 'success', message: 'Emissions calculated successfully' });
    } catch (error) {
      toastFromError({ error, title: 'Calculation failed' });
    } finally {
      setRecalculatingActivityId(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmModal({ isOpen: true, action: 'bulk-delete', activityId: null, activityInitiativeId: null });
  };

  const handleConfirmAction = async () => {
    const activityInitiativeId = confirmModal.activityInitiativeId ?? initiativeId;
    try {
      if (confirmModal.action === 'bulk-delete') {
        const count = selectedIds.size;
        await bulkDeleteActivities({ initiativeId, activityIds: Array.from(selectedIds), includeChildren }).unwrap();
        generateToast({ color: 'success', message: `Deleted ${count} activit${count === 1 ? 'y' : 'ies'} successfully` });
        setSelectedIds(new Set());
        setIsMultiSelectMode(false);
      } else if (confirmModal.action === 'delete') {
        if (!confirmModal.activityId) return;
        await deleteActivity({ initiativeId: activityInitiativeId, activityId: confirmModal.activityId }).unwrap();
        generateToast({ color: 'success', message: 'Activity deleted successfully' });
      } else if (confirmModal.action === 'recalculate') {
        if (!confirmModal.activityId) return;
        await triggerCalculation({
          initiativeId: activityInitiativeId,
          activityId: confirmModal.activityId,
          options: { recalculate: true },
        }).unwrap();
        generateToast({ color: 'success', message: 'Emissions recalculated successfully' });
      }
    } catch (error) {
      const title = confirmModal.action === 'bulk-delete' ? 'Bulk delete failed'
        : confirmModal.action === 'delete' ? 'Delete failed'
        : 'Recalculation failed';
      toastFromError({ error, title });
    }
    setConfirmModal(DEFAULT_CONFIRM_STATE);
  };

  const handleCancelConfirm = () => {
    setConfirmModal(DEFAULT_CONFIRM_STATE);
  };

  const handleViewCalculation = (activity: ActivityRecord) => {
    setSelectedActivity(activity);
  };

  const handleCloseCalculationDrawer = () => {
    setSelectedActivity(null);
  };

  const handleExport = async (activityIds: string[]) => {
    if (activityIds.length === 0) {
      return;
    }
    const result = await exportActivities({ initiativeId, activityIds });
    if ('data' in result && result.data) {
      downloadBlob(result.data.blob, result.data.filename);
      generateToast({
        color: 'success',
        message: `Exported ${activityIds.length} activit${activityIds.length === 1 ? 'y' : 'ies'} successfully`,
      });
      if (isMultiSelectMode) {
        setSelectedIds(new Set());
        setIsMultiSelectMode(false);
      }
    } else if ('error' in result) {
      toastFromError({ error: result.error, title: 'Export failed' });
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleToggleMultiSelect = () => {
    if (isMultiSelectMode) {
      setSelectedIds(new Set());
    }
    setIsMultiSelectMode(!isMultiSelectMode);
  };

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Activities</h4>
          <p className='tw:text-sm tw:text-muted-foreground'>Manage and track emission activity records</p>
        </div>
        <div className='tw:flex tw:gap-2 tw:items-center'>
          <IncludeChildrenToggle
            disabled={!hasChildren}
            includeChildren={includeChildren}
            onChange={setIncludeChildren}
          />
          <FiscalYearSelector selectedYear={selectedFiscalYear} onYearChange={handleFiscalYearChange} years={availableYears} />
          <Button
            onClick={() => history.push(newActivityUrl)}
            className='tw:text-white tw:border-0'
          >
            <Plus className='tw:size-4' />
            Activity
          </Button>
        </div>
      </div>

      {/* Data Quality + Category Breakdown */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-7 tw:gap-6 tw:mb-6'>
        <div className='tw:col-span-5'>
          <CategoryBreakdownPanel
            initiativeId={initiativeId}
            periodStart={filters.periodStart || undefined}
            periodEnd={filters.periodEnd || undefined}
            includeChildren={includeChildren}
          />
        </div>
        <div className='tw:col-span-2'>
          <DataQualityCard
            initiativeId={initiativeId}
            periodStart={filters.periodStart || undefined}
            periodEnd={filters.periodEnd || undefined}
            includeChildren={includeChildren}
          />
        </div>
      </div>

      {/* Activity List Card */}
      <Card>
        <CardHeader>
          <CardTitle>
            {activitiesData?.pagination.total ?? 0} activit{(activitiesData?.pagination.total ?? 0) === 1 ? 'y' : 'ies'}
            <span className='ms-2'>
              <Button
                variant={isMultiSelectMode ? 'secondary' : 'link'}
                size='xs'
                onClick={handleToggleMultiSelect}
                title={isMultiSelectMode ? 'Exit multi-select mode' : 'Select multiple activities'}
              >
                {isMultiSelectMode ? (
                  <>
                    <X className='tw:size-3' />
                    Cancel
                  </>
                ) : (
                  <>
                    <CheckSquare className='tw:size-3' />
                    Select
                  </>
                )}
              </Button>
              {isMultiSelectMode && selectedIds.size > 0 && (
                <>
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => handleExport(Array.from(selectedIds))}
                    disabled={isExporting}
                  >
                    <Download className='tw:size-3' />
                    {isExporting ? 'Exporting...' : `Export ${selectedIds.size} selected`}
                  </Button>
                  <Button
                    variant='outline'
                    size='xs'
                    className='tw:text-red-600 tw:hover:bg-red-50 tw:border-red-200'
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                  >
                    <Trash2 className='tw:size-3' />
                    {isBulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
                  </Button>
                </>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityFilters initiativeId={initiativeId} filters={filters} onChange={setFilters} />
          <ActivityTable
            activities={activities}
            isLoading={isLoading || isFetching}
            onEdit={(activityId) => {
              const activity = activities.find((a) => a._id === activityId);
              handleEdit(activityId, activity?.initiativeId ?? initiativeId);
            }}
            onDelete={handleDelete}
            onRecalculate={(activityId) => {
              const activity = activities.find((a) => a._id === activityId);
              handleRecalculate(activityId, activity?.initiativeId ?? initiativeId);
            }}
            onViewCalculation={handleViewCalculation}
            onExport={(id) => handleExport([id])}
            selectedIds={isMultiSelectMode ? selectedIds : undefined}
            onSelectionChange={isMultiSelectMode ? setSelectedIds : undefined}
            recalculatingActivityId={recalculatingActivityId}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            getReportingLevelLabel={includeChildren ? getReportingLevelLabel : undefined}
            queryPeriod={filters.periodStart && filters.periodEnd ? { start: filters.periodStart, end: filters.periodEnd } : undefined}
            emptyState={
              hasNonPeriodFilters ? (
                <EmptyStateCard
                  title='No matching activities'
                  message='No activities match your current filters. Try adjusting or clearing them.'
                  icon={<Inbox className='tw:size-10' />}
                  action={{ label: 'Clear filters', onClick: resetNonPeriodFilters, variant: 'outline' }}
                />
              ) : (
                <EmptyStateCard
                  title='No activities found'
                  message='Add your first activity to start tracking emissions.'
                  icon={<Inbox className='tw:size-10' />}
                  action={{ label: 'Add activity', to: newActivityUrl, variant: 'emerald' }}
                />
              )
            }
          />
          {activitiesData?.pagination && activitiesData.pagination.total > PAGE_SIZE && (
            <div className='tw:flex tw:justify-between tw:items-center tw:mt-3 tw:pt-3 tw:border-t'>
              <span className='tw:text-sm tw:text-muted-foreground'>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, activitiesData.pagination.total)} of{' '}
                {activitiesData.pagination.total} activities
              </span>
              <div className='tw:flex tw:items-center tw:gap-1'>
                <Button variant='outline' size='sm' onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
                  <ChevronLeft className='tw:size-4' />
                </Button>
                <span className='tw:text-sm tw:px-2'>
                  Page {page + 1} of {Math.ceil(activitiesData.pagination.total / PAGE_SIZE)}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= activitiesData.pagination.total}
                >
                  <ChevronRight className='tw:size-4' />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calculation Details Drawer */}
      <CalculationDetailsDrawer
        isOpen={selectedActivity !== null}
        toggle={handleCloseCalculationDrawer}
        initiativeId={selectedActivity?.initiativeId ?? initiativeId}
        activityId={selectedActivity?._id ?? null}
        onRecalculate={handleRecalculate}
        isRecalculating={isRecalculating}
        activityData={selectedActivity ? {
          activityType: selectedActivity.activityType,
          bulkImportId: selectedActivity.bulkImportId,
          quantity: selectedActivity.quantity,
          unit: selectedActivity.unit,
          region: selectedActivity.region,
          periodStart: selectedActivity.periodStart,
          periodEnd: selectedActivity.periodEnd,
          scope: selectedActivity.scope,
          fuelType: selectedActivity.fuelType,
          vehicleType: selectedActivity.vehicleType,
          refrigerantType: selectedActivity.refrigerantType,
          processType: selectedActivity.processType,
          regionFallback: selectedActivity.regionFallback,
          yearFallback: selectedActivity.yearFallback,
          calculationNotices: selectedActivity.calculationNotices,
        } : undefined}
        reportingLevelLabel={selectedActivity ? getReportingLevelLabel(selectedActivity.initiativeId) : undefined}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={
          confirmModal.action === 'bulk-delete' ? `Delete ${selectedIds.size} Activities`
          : confirmModal.action === 'delete' ? 'Delete Activity'
          : 'Recalculate Emissions'
        }
        content={
          confirmModal.action === 'bulk-delete'
            ? `Are you sure you want to delete ${selectedIds.size} activit${selectedIds.size === 1 ? 'y' : 'ies'}? This action cannot be undone.`
            : confirmModal.action === 'delete'
            ? 'Are you sure you want to delete this activity? This action cannot be undone.'
            : 'This will replace the existing calculation. Do you want to continue?'
        }
        confirmButton={
          <Button
            className={
              confirmModal.action === 'delete' || confirmModal.action === 'bulk-delete'
                ? 'tw:bg-red-600 tw:hover:bg-red-700 tw:text-white tw:border-0'
                : 'tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
            }
            onClick={handleConfirmAction}
            disabled={isDeleting || isRecalculating || isBulkDeleting}
          >
            {confirmModal.action === 'bulk-delete' && (isBulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size}`)}
            {confirmModal.action === 'delete' && (isDeleting ? 'Deleting...' : 'Delete')}
            {confirmModal.action === 'recalculate' && (isRecalculating ? 'Recalculating...' : 'Recalculate')}
          </Button>
        }
        handleCancel={handleCancelConfirm}
      />
    </Dashboard>
  );
};
